import { Injectable, Injector } from "@angular/core";
import { CareerValuation, CareerValuationBreezeModel } from "@common/ADAPT.Common.Model/organisation/career-valuation";
import { CulturalActivity, CulturalActivityBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityConfiguration, CulturalActivityConfigurationBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-activity-configuration";
import { CulturalActivitySupplementaryData, CulturalActivitySupplementaryDataBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-activity-supplementary-data";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { CulturalIndex, CulturalIndexBreezeModel } from "@common/ADAPT.Common.Model/organisation/cultural-index";
import { SpeedCatchup, SpeedCatchupBreezeModel } from "@common/ADAPT.Common.Model/organisation/speed-catchup";
import { Survey, SurveyBreezeModel } from "@common/ADAPT.Common.Model/organisation/survey";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { IBreezeModel } from "@common/lib/data/breeze-model.interface";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { RescheduleActivityDialogComponent } from "@org-common/lib/activities/reschedule-activity-dialog/reschedule-activity-dialog.component";
import { AfterOrganisationInitialisationObservable } from "@org-common/lib/organisation/after-organisation-initialisation.decorator";
import { BaseOrganisationService } from "@org-common/lib/organisation/base-organisation.service";
import { defer, Observable, of, switchMap } from "rxjs";
import { debounceTime, tap } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class CulturalActivityService extends BaseOrganisationService {
    private culturalActivityConfigurations?: CulturalActivityConfiguration[];

    public constructor(
        injector: Injector,
        rxjsBreezeService: RxjsBreezeService,
        private dialogService: AdaptCommonDialogService,
    ) {
        super(injector);

        // refresh configs when they change
        rxjsBreezeService.entityTypeChanged(CulturalActivityConfiguration).pipe(
            debounceTime(500),
            switchMap(() => this.initialiseService()),
        ).subscribe();
    }

    @AfterOrganisationInitialisationObservable
    public getCulturalActivityConfiguration(type: CulturalActivityType, teamId?: number) {
        const config = this.culturalActivityConfigurations?.find((c) => c.type === type
            && (teamId ? c.teamId === teamId : !c.teamId));
        return of(config);
    }

    @AfterOrganisationInitialisationObservable
    public getAllCulturalActivityConfigurations() {
        return of(this.culturalActivityConfigurations);
    }

    public createCulturalActivityConfiguration(type: CulturalActivityType, frequencyMonths: number, organisationId: number, teamId?: number) {
        return this.commonDataService.create(CulturalActivityConfigurationBreezeModel, { type, organisationId, teamId, frequencyMonths });
    }

    public getCulturalActivitiesForPerson(personId: number, startDate?: Date, endDate?: Date, excludeCompleted = false, types?: CulturalActivityType[]) {
        const predicate = new MethodologyPredicate<CulturalActivity>();

        if (excludeCompleted) {
            predicate.and(new MethodologyPredicate<CulturalActivity>("isCompleted", "==", false));
        }

        if (startDate) {
            predicate.and(new MethodologyPredicate<CulturalActivity>("date", ">=", startDate));
        }

        if (endDate) {
            predicate.and(new MethodologyPredicate<CulturalActivity>("date", "<=", endDate));
        }

        if (types) {
            predicate.and(new MethodologyPredicate<CulturalActivity>("type", "in", types));
        }

        const personPredicate = new MethodologyPredicate<CulturalActivity>("person1Id", "==", personId)
            .or(new MethodologyPredicate<CulturalActivity>("person2Id", "==", personId))
            .or(new MethodologyPredicate<CulturalActivity>("person1Id", "==", null)
                .and(new MethodologyPredicate<CulturalActivity>("person2Id", "==", null)));
        predicate.and(personPredicate);

        return this.commonDataService.getByPredicate(CulturalActivityBreezeModel, predicate);
    }

    public getCulturalActivitiesForActivePeople(types?: CulturalActivityType[]) {
        const predicate = new MethodologyPredicate<CulturalActivity>();

        if (types) {
            predicate.and(new MethodologyPredicate<CulturalActivity>("type", "in", types));
        }

        return this.commonDataService.getWithOptions(CulturalActivityBreezeModel, predicate.getKey(CulturalActivityBreezeModel.identifier), {
            predicate,
            // allow records without person2 to be included still (see QueryUtilities::filterForActive)
            isPartial: true,
            namedParams: {
                activeOnly: true,
            },
        });
    }

    public getUpcomingCulturalActivitiesForPersonIds(personIds: number[], types?: CulturalActivityType[]) {
        const predicate = new MethodologyPredicate<CulturalActivity>()
            .and(new MethodologyPredicate<CulturalActivity>("person1Id", "in", personIds)
                .or(new MethodologyPredicate<CulturalActivity>("person2Id", "in", personIds)))
            .and(new MethodologyPredicate<CulturalActivity>("isCompleted", "==", false));

        if (types) {
            predicate.and(new MethodologyPredicate<CulturalActivity>("type", "in", types));
        }

        return this.commonDataService.getByPredicate(CulturalActivityBreezeModel, predicate);
    }

    public createCulturalActivity(options: Partial<CulturalActivity>) {
        return this.commonDataService.create(CulturalActivityBreezeModel, options);
    }

    public getOrCreateCulturalActivitySupplementaryData(culturalActivity: CulturalActivity) {
        return this.getCulturalActivitySupplementaryData(culturalActivity).pipe(
            switchMap((supplementaryData) => supplementaryData
                ? of(supplementaryData)
                : this.createCulturalActivitySupplementaryData(culturalActivity)),
        );
    }

    public getCulturalActivitySupplementaryData(culturalActivity: CulturalActivity) {
        return culturalActivity.supplementaryData
            ? of(culturalActivity.supplementaryData)
            : this.commonDataService.getById(CulturalActivitySupplementaryDataBreezeModel, culturalActivity.culturalActivityId);
    }

    public getCulturalActivitySupplementaryDataForActivityIds(culturalActivityIds: number[]) {
        if (culturalActivityIds.length === 0) {
            return of([]);
        }

        const predicate = new MethodologyPredicate<CulturalActivitySupplementaryData>("culturalActivityId", "in", culturalActivityIds);

        return this.commonDataService.getByPredicate(CulturalActivitySupplementaryDataBreezeModel, predicate);
    }

    public createCulturalActivitySupplementaryData(culturalActivity: CulturalActivity) {
        return this.commonDataService.create(CulturalActivitySupplementaryDataBreezeModel, { culturalActivity });
    }

    public getEntityForCulturalActivity(culturalActivity: CulturalActivity): Observable<CareerValuation | CulturalIndex | SpeedCatchup | Survey | undefined> {
        if (!culturalActivity.entityId) {
            return of(undefined);
        }

        // check our cache locally first for any of these so we skip hitting the server.
        // we don't need the always fetching nav props for any of them
        const getLocalFirst = <T extends IBreezeEntity>(model: IBreezeModel<T>, id: number) =>
            of(this.commonDataService.getLocalModelEntityById(model, id)).pipe(
                switchMap((entity) => !entity ? this.commonDataService.getById(model, id) : of(entity)),
            );

        switch (culturalActivity.type) {
            case CulturalActivityType.CareerValuation:
                return getLocalFirst(CareerValuationBreezeModel, culturalActivity.entityId);
            case CulturalActivityType.CulturalIndex:
                return getLocalFirst(CulturalIndexBreezeModel, culturalActivity.entityId);
            case CulturalActivityType.PeerCatchUp:
                return getLocalFirst(SpeedCatchupBreezeModel, culturalActivity.entityId);
            case CulturalActivityType.TeamAssessment:
            case CulturalActivityType.ResilientBusinessAssessment:
            case CulturalActivityType.EmployeeEngagementAssessment:
                return getLocalFirst(SurveyBreezeModel, culturalActivity.entityId);
        }
    }

    public rescheduleActivity(culturalActivity: CulturalActivity, createSuppData = true) {
        // defer turns this from (Observable<...> | Observable<undefined>) to Observable<... | undefined>
        // which then works with the switchMap
        const suppData$ = defer(() => createSuppData
            ? this.getOrCreateCulturalActivitySupplementaryData(culturalActivity)
            : of(undefined));

        return suppData$.pipe(
            switchMap(() => this.dialogService.open(RescheduleActivityDialogComponent, culturalActivity)),
        );
    }

    protected organisationInitialisationActions() {
        return [
            this.initialiseService(),
        ];
    }

    private initialiseService() {
        return this.commonDataService.getAll(CulturalActivityConfigurationBreezeModel).pipe(
            tap((culturalConfigs) => this.culturalActivityConfigurations = culturalConfigs),
        );
    }
}
